import * as React from 'react'
import { forwardRef } from 'react'
import { cn } from '@/lib/css-utils'
import { ErrorMessage } from '@/components/ui/error-message'

interface FormControlProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: string | React.ReactNode
  subLabel?: string | React.ReactNode
  helperText?: string | React.ReactNode
  errorText?: string
}

const inputErrorClasses = 'border-input-error placeholder-input-error'

/**
 * Component to wrap form inputs
 * Will add a label (if present) and associate it with the input
 * Will add error text (if present) and apply error styles to input
 */
const FormControl = forwardRef(
  (
    {
      label,
      errorText,
      subLabel,
      helperText,
      children,
      ...rest
    }: React.PropsWithChildren<FormControlProps>,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const inputId = React.useId()
    const hasError = Boolean(errorText)

    const childrenWithProps = React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        const _child = child as React.ReactElement<
          React.HTMLAttributes<HTMLElement>
        >
        return React.cloneElement(_child, {
          className: cn(
            _child.props.className,
            hasError ? inputErrorClasses : null,
          ),
          'aria-invalid': hasError,
          id: inputId,
        })
      }
      return child
    })

    return (
      <div
        {...rest}
        ref={ref}
        // adding error class for dropdown
        className={cn(rest.className, hasError ? 'form-control--error' : null)}
      >
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium mb-0.5 text-input-label"
          >
            {label}
          </label>
        )}
        {subLabel && (
          <p className="text-sm text-input-sublabel mb-0.5">{subLabel}</p>
        )}
        {childrenWithProps}
        {errorText ? <ErrorMessage>{errorText}</ErrorMessage> : null}
        {helperText ? (
          <p className="text-xs text-copy-accent">{helperText}</p>
        ) : null}
      </div>
    )
  },
)

FormControl.displayName = 'FormControl'
export { FormControl }
